import axios from 'axios';
import config, { isDemoHost } from '../config/api.config';
import { logger } from '../utils/logger';
import GoftinoService from './goftinoService';
// Note: Using plain axios here instead of apiClient to avoid circular dependency

const DESK_URL = 'https://desk.smartx.ir';
const DESK_API_URL = 'https://deskapi.smartx.ir';
const INSIGHT_PRODUCT_ID = '1';
const BRAND_ID = '1';
const API_BASE_URL = config.API_URL;

interface AuthTokens {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  user?: any;
}

interface MagicLinkResponse {
  token: string;
  redirect_url?: string;
}

interface UserInfo {
  id: string;
  email: string;
  mobile: string;
  name: string;
  branch_id: string;
  permissions?: string[];
}

/** Accept snake_case, camelCase, or nested `{ data: ... }` Desk/API shapes. */
function normalizeAuthTokens(payload: any): AuthTokens {
  const raw = payload?.data && (payload.data.access_token || payload.data.token || payload.data.refresh_token || payload.data.refreshToken)
    ? payload.data
    : payload;

  const access_token = raw?.access_token || raw?.token || raw?.accessToken;
  const refresh_token = raw?.refresh_token || raw?.refreshToken;

  return {
    access_token,
    refresh_token,
    token_type: raw?.token_type || raw?.tokenType || 'bearer',
    expires_in: raw?.expires_in ?? raw?.expiresIn,
    user: raw?.user ?? payload?.user,
  };
}

class AuthService {
  private static TOKEN_KEY = 'insight_auth_token';
  private static REFRESH_TOKEN_KEY = 'insight_refresh_token';
  private static USER_KEY = 'insight_user_info';
  private static refreshInFlight: Promise<AuthTokens> | null = null;

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static getUserInfo(): UserInfo | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        // console.log(userStr)
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  static saveTokens(tokens: AuthTokens | Record<string, any>): void {
    const normalized = normalizeAuthTokens(tokens);
    if (!normalized.access_token) {
      throw new Error('Cannot save auth tokens: access_token missing');
    }
    localStorage.setItem(this.TOKEN_KEY, normalized.access_token);
    if (normalized.refresh_token) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, normalized.refresh_token);
    }
  }

  static saveUserInfo(user: UserInfo): void {
    console.log(user)
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    // Don't clear brand/branch selections - preserve user's choices
    // localStorage.removeItem('insight_selected_brand');
    // localStorage.removeItem('insight_selected_branch');
  }
  
  static async getCurrentUser(): Promise<UserInfo | null> {
    try {
      const token = this.getToken();
      if (!token) {
        return null;
      }
      
      const response = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        // The /auth/me endpoint returns data directly, not wrapped in { data: ... }
        const userData = response.data.data || response.data;
        logger.debug('User data from /auth/me', 'AuthService', userData);
        const userInfo: UserInfo = {
          id: userData.id || userData.user_id, // backend might use user_id
          email: userData.email,
          mobile: userData.mobile || "",
          name: userData.name || userData.email.split('@')[0],
          branch_id: userData.branch_id,
          permissions: userData.permissions || []
        };
        return userInfo;
      }
      
      return null;
    } catch (error) {
      logger.error('Failed to get current user', 'AuthService', error);
      throw error;
    }
  }

  static redirectToDesk(branchId?: string): void {
    if (config.STAGE_NO_AUTH || isDemoHost()) {
      window.location.href = '/demo-login';
      return;
    }

    const currentUrl = window.location.href;
    const returnUrl = encodeURIComponent(currentUrl);
    
    // Parse current URL to extract brandId and branchId from query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlBrandId = urlParams.get('brandId');
    const urlBranchId = urlParams.get('branchId') || branchId;
    
    // Build desk URL with all parameters
    let deskUrl = `${DESK_URL}?returnUrl=${returnUrl}`;
    
    if (urlBrandId) {
      deskUrl += `&brandId=${urlBrandId}`;
    }
    
    if (urlBranchId) {
      deskUrl += `&branchId=${urlBranchId}`;
    }
    
    window.location.href = deskUrl;
  }

  /** On staging, Magic Link is not registered — send users to production Insight. */
  static redirectToLogin(branchId?: string): void {
    if (config.STAGE_NO_AUTH) {
      return;
    }
    if (isDemoHost()) {
      window.location.href = '/demo-login';
      return;
    }
    if (config.isStagingHost()) {
      window.location.href = config.INSIGHT_PROD_URL;
      return;
    }
    this.redirectToDesk(branchId);
  }

  static async exchangeRefreshKey(refreshKey: string): Promise<AuthTokens> {
    if (config.STAGE_NO_AUTH) {
      throw new Error('Auth disabled: refresh exchange not available');
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refresh_token: refreshKey,
      });

      const tokens = normalizeAuthTokens(response.data);
      if (!tokens.access_token || !tokens.refresh_token) {
        logger.error(
          'Refresh exchange response missing tokens',
          'AuthService',
          { keys: response.data ? Object.keys(response.data) : [] }
        );
        throw new Error('Refresh exchange did not return access and refresh tokens');
      }

      this.saveTokens(tokens);

      if (tokens.user) {
        this.saveUserInfo(tokens.user);
      }

      // Same Desk quirk as magic-link: rotate once more so the stored refresh
      // token is a confirmed post-refresh credential (needed for later handoff).
      try {
        logger.info('Rotating tokens after env-handoff exchange...', 'AuthService');
        const rotated = await this.refreshToken();
        return {
          ...rotated,
          user: rotated.user || tokens.user,
        };
      } catch (rotateError) {
        logger.error(
          'Post-handoff token rotation failed; restoring exchange tokens',
          'AuthService',
          rotateError
        );
        // refreshToken() may have cleared storage on failure — restore exchange result.
        this.saveTokens(tokens);
        if (tokens.user) {
          this.saveUserInfo(tokens.user);
        }
        return tokens;
      }
    } catch (error) {
      logger.error('Failed to exchange refresh key', 'AuthService', error);
      throw error;
    }
  }

  static getEnvHandoffUrl(
    targetOrigin: 'prod' | 'stage',
    options?: { brandId?: number; branchId?: number }
  ): string {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available for environment handoff');
    }

    const base =
      targetOrigin === 'stage' ? config.INSIGHT_STAGE_URL : config.INSIGHT_PROD_URL;
    const params = new URLSearchParams();
    params.set('refreshKey', refreshToken);
    if (options?.brandId) {
      params.set('brandId', String(options.brandId));
    }
    if (options?.branchId) {
      params.set('branchId', String(options.branchId));
    }

    return `${base}/auth/env-handoff?${params.toString()}`;
  }

  static async validateMagicToken(magicToken: string, brandId?: string, branchId?: string): Promise<any> {
    try {
      const params: any = {
        token: magicToken
      };
      
      // Add brandId if provided and valid
      if (brandId && brandId !== '0') {
        params.brandId = parseInt(brandId, 10);
      }
      
      // Add branchId if provided and valid
      if (branchId && branchId !== '0') {
        params.branchId = parseInt(branchId, 10);
      }
      
      const response = await axios.get(`${API_BASE_URL}/auth/validate-magic-token`, {
        params
      });
      
      const data = normalizeAuthTokens(response.data);
      // Preserve redirect_url from the raw payload
      const redirect_url = response.data?.redirect_url;
      this.saveTokens(data);
      
      // Check if user data is valid (not empty)
      const isUserDataValid = data.user && data.user.id && data.user.email;
      
      if (isUserDataValid) {
        // Ensure mobile field has a default value
        if (!data.user.mobile) {
          data.user.mobile = "";
        }
        console.log("data.user:",data.user)
        this.saveUserInfo(data.user);
      } else {
        // If user data is empty or invalid, fetch it from /auth/me
        logger.info('User data from magic token is empty, fetching from /auth/me...', 'AuthService');
        try {
          const userInfo = await this.getCurrentUser();
          console.log("userInfo:",userInfo)
          if (userInfo) {
            this.saveUserInfo(userInfo);
            data.user = userInfo;
          }
        } catch (error) {
          logger.error('Failed to fetch user info after magic token validation', 'AuthService', error);
        }
      }
      
      // Immediately refresh the token after receiving it from magic link
      try {
        logger.info('Immediately refreshing token after magic link validation...', 'AuthService');
        const refreshedTokens = await this.refreshToken();
        logger.info('Token refreshed successfully after magic link validation', 'AuthService');
        
        // Return the refreshed token data, but keep the original user and redirect info
        return {
          ...refreshedTokens,
          user: data.user,
          redirect_url
        };
      } catch (refreshError) {
        logger.error('Failed to refresh token immediately after magic link validation', 'AuthService', refreshError);
        // refreshToken() may clear storage on failure — restore magic-link tokens.
        this.saveTokens(data);
        if (data.user) {
          this.saveUserInfo(data.user);
        }
        return { ...data, redirect_url };
      }
    } catch (error) {
      logger.error('Failed to validate magic token', 'AuthService', error);
      throw error;
    }
  }

  static async refreshToken(): Promise<AuthTokens> {
    if (config.STAGE_NO_AUTH) {
      throw new Error('Stage mode: refresh not required');
    }

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshInFlight = (async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken
        });

        const tokens = normalizeAuthTokens(response.data);
        if (!tokens.access_token || !tokens.refresh_token) {
          throw new Error('Token refresh response missing access or refresh token');
        }

        this.saveTokens(tokens);

        if (tokens.user) {
          this.saveUserInfo(tokens.user);
        }

        return tokens;
      } catch (error: any) {
        logger.error('Failed to refresh token', 'AuthService', error);

        const status = error.response?.status;
        // Only wipe the session on auth failures — keep tokens on network/5xx so
        // callers (magic-link / env-handoff rotation) can restore or retry.
        if (status === 401 || status === 403) {
          this.clearAuth();
          if (isDemoHost()) {
            window.location.href = '/demo-login';
          } else if (
            error.response?.data?.detail?.includes('re-authentication through Desk SSO') &&
            !config.STAGE_NO_AUTH
          ) {
            this.redirectToLogin();
          }
        }

        throw error;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  static async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      logger.error('Logout error', 'AuthService', error);
    } finally {
      // Clear Goftino user info before clearing auth
      GoftinoService.clearUser();
      this.clearAuth();
    }
  }

  static async devLogin(): Promise<AuthTokens> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/dev-login`);
      
      const tokens = response.data;
      this.saveTokens(tokens);
      
      if (tokens.user) {
        this.saveUserInfo(tokens.user);
      }
      
      return tokens;
    } catch (error) {
      logger.error('Failed to login with dev credentials', 'AuthService', error);
      throw error;
    }
  }

  static async fetchUserInfo(brandId?: number, branchId?: number): Promise<any> {
    if (config.STAGE_NO_AUTH) {
      return {};
    }

    if (isDemoHost()) {
      const cached = this.getUserInfo();
      return {
        isAdmin: true,
        userFullName: cached?.name || 'کاربر نمایشی',
        email: cached?.email || 'demo@smartx.ir',
        mobile: cached?.mobile || '',
        productRoles: [{ roleName: 'ادمین' }],
      };
    }

    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      logger.info('Fetching user info', 'AuthService', { brandId, branchId });

      // Build query parameters
      const params = new URLSearchParams();
      if (brandId) params.append('brandId', brandId.toString());
      // Always send branchId, use 0 if not provided
      params.append('branchId', (branchId || 0).toString());

      const response = await axios.get(`${API_BASE_URL}/auth/user-info?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const userData = response.data;
      logger.info('User info received', 'AuthService', { 
        hasUserData: !!userData,
        userDataKeys: Object.keys(userData || {})
      });
      
      // Fetch phone number from separate endpoint
      try {
        logger.info('Fetching phone number from separate endpoint', 'AuthService');
        const phoneResponse = await axios.get(`${API_BASE_URL}/auth/phone-number`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        userData.mobile = phoneResponse.data.mobile || "";
        logger.info('Phone number fetched successfully', 'AuthService', { 
          mobile: userData.mobile,
          hasMobile: !!userData.mobile
        });
      } catch (phoneError) {
        logger.error('Failed to fetch phone number', 'AuthService', phoneError);
        userData.mobile = "";
      }
      
      // Update localStorage with the phone number
      const currentUserInfo = this.getUserInfo();
      logger.info('Current user info from localStorage', 'AuthService', { 
        hasCurrentUser: !!currentUserInfo,
        currentMobile: currentUserInfo?.mobile
      });
      
      if (currentUserInfo) {
        currentUserInfo.mobile = userData.mobile;
        this.saveUserInfo(currentUserInfo);
        logger.info('Updated localStorage with mobile number', 'AuthService', { 
          mobile: currentUserInfo.mobile
        });
      } else {
        logger.warn('No current user info in localStorage to update', 'AuthService');
      }

      return userData;
    } catch (error: any) {
      logger.error('Failed to fetch user info', 'AuthService', error);
      
      // Check if it's a token expiration error
      if (error.response?.status === 401 && 
          (error.response?.data?.detail === 'token_expired' || 
           error.response?.data?.detail?.includes('expired'))) {
        
        logger.info('User info request failed due to expired token, attempting refresh...', 'AuthService');
        
        try {
          // Try to refresh the token
          const newTokens = await this.refreshToken();
          
          // Retry the request with the new token
          const retryParams = new URLSearchParams();
          if (brandId) retryParams.append('brandId', brandId.toString());
          retryParams.append('branchId', (branchId || 0).toString());
          
          const retryResponse = await axios.get(`${API_BASE_URL}/auth/user-info?${retryParams}`, {
            headers: {
              Authorization: `Bearer ${newTokens.access_token}`
            }
          });
          
          const retryUserData = retryResponse.data;
          
          // Fetch phone number from separate endpoint
          try {
            const phoneResponse = await axios.get(`${API_BASE_URL}/auth/phone-number`, {
              headers: {
                Authorization: `Bearer ${newTokens.access_token}`
              }
            });
            retryUserData.mobile = phoneResponse.data.mobile || "";
          } catch (phoneError) {
            logger.error('Failed to fetch phone number on retry', 'AuthService', phoneError);
            retryUserData.mobile = "";
          }
          
          // Update localStorage with the phone number
          const currentUserInfo = this.getUserInfo();
          if (currentUserInfo) {
            currentUserInfo.mobile = retryUserData.mobile;
            this.saveUserInfo(currentUserInfo);
          }
          
          return retryUserData;
        } catch (refreshError) {
          logger.error('Token refresh failed during user info fetch', 'AuthService', refreshError);
          this.clearAuth();
          if (isDemoHost()) {
            window.location.href = '/demo-login';
          } else if (!config.STAGE_NO_AUTH) {
            this.redirectToLogin();
          }
          throw refreshError;
        }
      }
      
      throw error;
    }
  }

  static saveBrandSelection(brandId: number): void {
    localStorage.setItem('insight_selected_brand', brandId.toString());
  }

  static saveBranchSelection(branchId: number): void {
    localStorage.setItem('insight_selected_branch', branchId.toString());
  }

  static getSavedBrandId(): number | null {
    const brandId = localStorage.getItem('insight_selected_brand');
    return brandId ? parseInt(brandId, 10) : null;
  }

  static getSavedBranchId(): number | null {
    const branchId = localStorage.getItem('insight_selected_branch');
    return branchId ? parseInt(branchId, 10) : null;
  }

  static clearBranchSelection(): void {
    localStorage.removeItem('insight_selected_branch');
  }

  static clearSelections(): void {
    localStorage.removeItem('insight_selected_brand');
    localStorage.removeItem('insight_selected_branch');
  }

  static clearAll(): void {
    // Clear everything including selections
    this.clearAuth();
    this.clearSelections();
  }
}

export default AuthService;
